import React, { useCallback, useEffect, useMemo, useState } from "react";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ClearIcon from '@mui/icons-material/Clear'; // Import ClearIcon
import Cleave from "cleave.js/react";
import Validation from "./validation/Validation";
import styles from "./AddPatient.module.scss";
import questionsStyles from "./Questions.module.scss";
import TimeKeeper from "react-timekeeper";
import { useRef } from "react";
import classNames from "classnames";
import HtmlTooltip from "../../../../shared/HtmlTooltip";
import { getAllQuestions } from "../../../../../services/api/question.api";
import { getValidations } from "../../../../../services/validations.service";
import api from "../../../../../services/api/api";
import _ from "lodash";
import { IconButton, InputAdornment, MenuItem, Popover, Select, TextField, Box } from "@mui/material";
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import QuestionLabel from "./QuestionLabel";
import AddPatientTextPopup from "./AddPatientTextPopup";
import { QUESTION_INPUT_TYPE } from "../../../../../types/question.type";
import { toDisplayTime } from "../../../../../utilis/date-formats";
import AiChatSidebar from "./AiChatSidebar";
import FloatingAiIcon from "./FloatingAiIcon";
import { useSelector } from "react-redux";
import useFacilityId from "../../../../../hooks/useFacilityId";

// Common constant for Medicare label - can be easily changed in the future
const MEDICARE_LABEL = "Medicare";

// Helper function to get the effective value (user input takes priority over auto-fill)
const getEffectiveValue = (currentValue, autoFillValue) => {
    // If user has entered a value, use it (even if it's empty string)
    if (currentValue !== undefined && currentValue !== null) {
        return currentValue;
    }
    // Otherwise, use auto-fill value if it's not empty/null
    if (autoFillValue && autoFillValue !== '' && autoFillValue !== null) {
        return autoFillValue;
    }
    // Default to empty string
    return '';
};

// Helper function to check if any question has AI enabled
const hasAiEnabledQuestions = (questions, questionAiStatus = {}) => {
    return questions && questions.length > 0 && questions.some(q => questionAiStatus[q.question?._id] === true);
};

const AUTO_FILL_SOURCE_TYPE_LABELS = {
    admission: 'Admission',
    return: 'Return',
    hospitalTransfer: 'Hospital Transfer',
    safeDischarge: 'Safe Discharge',
    SNF: 'SNF',
    AMA: 'AMA',
    deceased: 'Deceased',
};

const formatAutoFillDisplayValue = (question, rawValue) => {
    if (rawValue == null || rawValue === '') return '';
    const value = String(rawValue);

    // Keep existing entered time strings (like 12:30pm) as-is.
    if (question?.type === 'time') {
        // If backend sends a full datetime/ISO for a time question, normalize it.
        if (value.includes('T') || value.includes('-')) {
            const formattedTime = toDisplayTime(value, "hh:mm A");
            return formattedTime && formattedTime !== '-' ? formattedTime : value;
        }
        return value;
    }

    if (question?.type === 'date') {
        // Match existing date rendering behavior in this file (MM/DD/YYYY).
        const formattedDate = toDisplayTime(value, "MM/DD/YYYY");
        return formattedDate && formattedDate !== '-' ? formattedDate : value;
    }

    return value;
};

const getAutoFillInfo = (question) => {
    if (!question?.autoFillEnabled) return null;
    const sourceTypeLabel = AUTO_FILL_SOURCE_TYPE_LABELS[question?.sourceTransitionType] || question?.sourceTransitionType || 'Unknown';
    return {
        sourceLine: `Prior ${sourceTypeLabel}`,
        label: question?.sourceQuestion?.label || question?.sourceQuestion?.accessor || question?.accessor || '',
        value: question?.autoFillValueLabel ? question?.autoFillValueLabel : formatAutoFillDisplayValue(question, question?.autoFillValue),
    };
};

const InvisibleOptionsDropdown = ({
    dataInputLabel,
    onSelect,
    anchorEl,
    handleClose,
}) => {
    const options = useMemo(() => {
        if (!dataInputLabel) return [];
        return dataInputLabel.split(',').map(opt => opt.trim()).filter(opt => opt !== '');
    }, [dataInputLabel]);

    return (
        <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
            <Box sx={{ padding: '4px', minWidth: '180px', maxHeight: '250px', overflow: 'auto' }}>
                {options.length > 0 ? (
                    options.map((option, index) => (
                        <MenuItem
                            key={`${option}-${index}`}
                            sx={{
                                fontSize: '13px',
                                fontFamily: 'mont reg',
                                '&:hover': { backgroundColor: '#f5f5f5' }
                            }}
                            onClick={() => {
                                onSelect(option);
                                handleClose();
                            }}
                        >
                            {option}
                        </MenuItem>
                    ))
                ) : (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                        <label className="ffmr fs12" style={{ color: '#8f8f8f' }}>No options found</label>
                    </Box>
                )}
            </Box>
        </Popover>
    );
};

const SelectInputField = ({
    question,
    allFields,
    setAllFieldsValue,
    checkError = false,
    handleClickExpand,
    handleAddHighlight,
    questionAiStatus = {},
}) => {
    const isExpand = useMemo(() => {
        return question.accessor === "notes" || question?.defaultOrder === undefined;
    }, [question]);

    const isHighLightInfo = useMemo(() => {
        return allFields?.highlighter?.find(item => item.fieldName === question.accessor)?.color;
    }, [allFields?.highlighter, question.accessor]);

    const options = useMemo(() => {
        let baseOptions = [...(question?.customQuestionOptions || [])];
        if (question?.dataInputLabel) {
            const extraOptions = question.dataInputLabel.split(',').map(opt => opt.trim()).filter(opt => opt !== '');
            baseOptions = [...baseOptions, ...extraOptions];
        }
        return Array.from(new Set(baseOptions.filter(option => option && typeof option === 'string')))
            .sort((a, b) => a.localeCompare(b));
    }, [question?.customQuestionOptions, question?.dataInputLabel]);

    const handleChange = (e) => {
        setAllFieldsValue(question.accessor, e.target.value);
    };

    return (
        <div className={`m-b-20 inputWrpr`}>
            <QuestionLabel
                handleAddHighlight={handleAddHighlight}
                accessor={question.accessor}
                allFields={allFields}
                aiEnabled={questionAiStatus[question._id] || false}
                autoFillEnabled={question?.autoFillEnabled || false}
                autoFillInfo={getAutoFillInfo(question)}
            >
                <label
                    className={`ffmr fs12 ${checkError && !allFields[question.accessor] && question?.isRequired === true
                        ? "error"
                        : null
                        }`}
                >
                    {question?.order?.label || question.label}
                </label>
            </QuestionLabel>
            <Select
                fullWidth
                value={getEffectiveValue(allFields[question.accessor], question?.autoFillValue)}
                onChange={handleChange}
                variant="standard"
                disableUnderline
                InputProps={{
                    ...isExpand && {
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    sx={{ marginRight: "-2px" }}
                                    onClick={() => handleClickExpand(allFields[question.accessor], question.accessor)}
                                    edge="end"
                                    size="small"
                                    disableRipple={true}
                                >
                                    <OpenInFullIcon sx={{ color: "#8f8f8f" }} />
                                </IconButton>
                            </InputAdornment>
                        ),
                    },
                }}
                sx={{
                    height: "40px",
                    borderRadius: "4px",
                    fontSize: "14px",
                    fontFamily: "mont reg",
                    '& .MuiSelect-select': {
                        height: "20px",
                        borderRadius: "4px",
                        border: isHighLightInfo ? `1px solid ${isHighLightInfo} !important` : 'none !important',
                        padding: '10px',
                        '&:focus': {
                            backgroundColor: 'transparent !important',
                            outline: 'none !important',
                        },
                        '&:focus-visible': {
                            backgroundColor: 'transparent !important',
                            outline: 'none !important',
                        }
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                    },
                    '& .MuiSelect-icon': {
                        color: '#8f8f8f',
                    },
                }}
            >
                {options.map((option) => (
                    <MenuItem key={option} value={option}>
                        {option}
                    </MenuItem>
                ))}
            </Select>
        </div>
    );
};

const NumberAndSelectQuestion = ({
    question,
    allFields,
    setAllFieldsValue,
    checkError = false,
    handleAddHighlight,
    questionAiStatus = {},
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const currentValue = getEffectiveValue(allFields[question?.accessor], question?.autoFillValue);
    const previousValue = useRef(currentValue || '');

    const options = useMemo(() => {
        const limitedOptions = question.customQuestionOptions
            ?.filter((ele) => ele?.type === "limitedAnswers")
            .map((item) => (
                <MenuItem
                    key={item?.option}
                    value={item?.option}
                    onClick={() => handleOptionClick(item?.option)}
                >
                    {item?.option}
                </MenuItem>
            )) || [];

        // For NUMBER_RANGE_LIMITED_ANSWERS, only show the actual predefined options
        // Do NOT generate range options - only use what's explicitly defined
        return limitedOptions;
    }, [question.customQuestionOptions, setAllFieldsValue, question.accessor]); // eslint-disable-line react-hooks/exhaustive-deps

    const isHighLightInfo = useMemo(() => {
        return allFields?.highlighter?.find((item) => item.fieldName === question.accessor)?.color;
    }, [allFields, question.accessor]);

    const handleArrowClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleOptionClick = (option) => {
        setAllFieldsValue(question.accessor, option);
        previousValue.current = option;
        handleClose();
    };

    const handleChange = (e) => {
        const value = e.target.value;

        // Check if it's a valid limitedAnswers option
        const isValidLimitedOption = question.customQuestionOptions
            ?.filter((ele) => ele?.type === 'limitedAnswers')
            .some((item) => item?.option === value);

        // Check if it's a valid number
        const isNumber = /^\d+$/.test(value);

        // For NUMBER_RANGE_LIMITED_ANSWERS, accept any number OR predefined options
        if (isValidLimitedOption || isNumber || value === '') {
            setAllFieldsValue(question.accessor, value);
            if (value !== '') {
                previousValue.current = value;
            }
        } else {
            setAllFieldsValue(question.accessor, previousValue.current);
            e.target.value = previousValue.current;
        }
    };

    const handleClear = () => {
        setAllFieldsValue(question.accessor, '');
        previousValue.current = '';
    };

    return (
        <>
            <div className={`m-b-20 inputWrpr`}>
                <QuestionLabel
                    handleAddHighlight={handleAddHighlight}
                    accessor={question.accessor}
                    allFields={allFields}
                    aiEnabled={questionAiStatus[question._id] || false}
                    autoFillEnabled={question?.autoFillEnabled || false}
                    autoFillInfo={getAutoFillInfo(question)}
                >
                    <label
                        className={`ffmr fs12 ${checkError && !allFields[question.accessor] && question?.isRequired === true ? 'error' : null
                            }`}
                    >
                        {question?.order?.label || question.label}
                    </label>
                </QuestionLabel>
                <TextField
                    fullWidth
                    value={getEffectiveValue(allFields[question.accessor], question?.autoFillValue)}
                    onChange={handleChange}
                    autoComplete="off"
                    type={'text'}
                    InputProps={{
                        startAdornment: (
                            <>
                                <InputAdornment position="end">
                                    <IconButton onClick={handleArrowClick} size="small">
                                        <ArrowDropDownIcon fontSize="10px" />
                                    </IconButton>
                                </InputAdornment>
                                {currentValue && question.customQuestionOptions
                                    ?.filter((ele) => ele?.type === 'limitedAnswers')
                                    .some((item) => item?.option === currentValue) && (
                                        <InputAdornment position="end">
                                            <IconButton onClick={handleClear} size="small">
                                                <ClearIcon fontSize="10px" />
                                            </IconButton>
                                        </InputAdornment>
                                    )}
                            </>
                        ),
                    }}
                    sx={{
                        input: {
                            borderRadius: '4px',
                            border: 'none !important',
                        },
                        '& .MuiInputBase-root': {
                            height: '40px',
                            borderRadius: '4px',
                            ...isHighLightInfo && { border: `1px solid ${isHighLightInfo} !important` },
                        },
                    }}
                />
            </div>
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                {options}
            </Popover>
        </>
    );
};

const TextQuestion = ({ question, allFields, setAllFieldsValue, checkError = false, handleClickExpand, handleAddHighlight, isOnlyNumber = false, customQuestion = false, questionAiStatus = {} }) => {
    const [anchorEl, setAnchorEl] = useState(null);

    const isExpand = useMemo(() => {
        if (customQuestion) {
            return false;
        }
        // eslint-disable-next-line eqeqeq
        return question.accessor === "notes" || question?.defaultOrder == undefined;
    }, [question, customQuestion]);

    const isHighLightInfo = useMemo(() => {
        return allFields?.highlighter?.find(item => item.fieldName === question.accessor)?.color;
        // eslint-disable-next-line
    }, [allFields]);

    const handleArrowClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const onSelectOption = (val) => {
        setAllFieldsValue(question.accessor, val);
    };

    return (
        <div className={`m-b-20 inputWrpr`}>
            <QuestionLabel
                handleAddHighlight={handleAddHighlight}
                accessor={question.accessor}
                allFields={allFields}
                aiEnabled={questionAiStatus[question._id] || false}
                autoFillEnabled={question?.autoFillEnabled || false}
                autoFillInfo={getAutoFillInfo(question)}
            >
                <label
                    className={`ffmr fs12 ${checkError && !allFields[question.accessor] && question?.isRequired === true
                        ? "error"
                        : null
                        }`}
                >
                    {question?.order?.label || question.label}
                </label>
            </QuestionLabel>
            <TextField
                fullWidth
                value={getEffectiveValue(allFields[question.accessor], question?.autoFillValue)}
                onChange={e => setAllFieldsValue(question.accessor, e.target.value)}
                variant="standard"
                autoComplete="off"
                type={(() => {
                    if (!isOnlyNumber) return 'text';
                    const val = getEffectiveValue(allFields[question.accessor], question?.autoFillValue);
                    if (!val || !isNaN(val)) return 'number';
                    return 'text';
                })()}
                InputProps={{
                    disableUnderline: true,
                    ...question.invisibleAnswerOptions && {
                        startAdornment: (
                            <InputAdornment position="start">
                                <IconButton onClick={handleArrowClick} size="small" sx={{ ml: 0.5, p: 0 }}>
                                    <ArrowDropDownIcon sx={{ fontSize: '18px', color: '#8f8f8f' }} />
                                </IconButton>
                            </InputAdornment>
                        )
                    },
                    endAdornment: (
                        <InputAdornment position="end">
                            {(() => {
                                const val = getEffectiveValue(allFields[question.accessor], question?.autoFillValue);
                                const invisibleLabels = question.invisibleAnswerOptions && question.dataInputLabel
                                    ? question.dataInputLabel.split(',').map(opt => opt.trim()).filter(opt => opt !== '')
                                    : [];
                                const isInvisibleOption = val && invisibleLabels.includes(val);

                                if (isInvisibleOption) {
                                    return (
                                        <IconButton
                                            onClick={() => setAllFieldsValue(question.accessor, '')}
                                            size="small"
                                            sx={{ marginRight: isExpand ? "-8px" : "0px" }}
                                        >
                                            <ClearIcon sx={{ fontSize: '14px', color: '#8f8f8f' }} />
                                        </IconButton>
                                    );
                                }
                                return null;
                            })()}
                            {isExpand && (
                                <IconButton
                                    sx={{ marginRight: "-2px" }}
                                    onClick={() => handleClickExpand(allFields[question.accessor], question.accessor)}
                                    edge="end"
                                    size="small"
                                    disableRipple={true}
                                >
                                    <OpenInFullIcon sx={{ color: "#8f8f8f" }} />
                                </IconButton>
                            )}
                        </InputAdornment>
                    )
                }}
                sx={{
                    "input": {
                        borderRadius: "4px",
                        border: "none !important",
                        padding: "0 10px !important",
                        '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                            '-webkit-appearance': 'none',
                            margin: 0,
                        },
                        '&[type=number]': {
                            '-moz-appearance': 'textfield',
                        },
                    },
                    '& .MuiInputBase-root': {
                        height: "40px",
                        borderRadius: "4px",
                        border: isHighLightInfo ? `1px solid ${isHighLightInfo} !important` : '1px solid #e4e4e4 !important',
                        paddingLeft: question.invisibleAnswerOptions ? "4px" : "10px",
                    },
                }}
            />
            {question.invisibleAnswerOptions && (
                <InvisibleOptionsDropdown
                    dataInputLabel={question.dataInputLabel}
                    anchorEl={anchorEl}
                    handleClose={handleClose}
                    onSelect={onSelectOption}
                />
            )}
        </div>
    );
};

const DateQuestion = ({ question, allFields, setAllFieldsValue, checkError = false, handleAddHighlight, setIsReadmissionValid, isReadmissionValid, setIsDateOfADTValid, isDateOfADTValid, questionAiStatus = {} }) => {
    const [anchorEl, setAnchorEl] = useState(null);

    const isHighLightInfo = useMemo(() => {
        return allFields?.highlighter?.find(item => item.fieldName === question.accessor)?.color;
        // eslint-disable-next-line
    }, [allFields]);

    const hasAutoFilled = useRef(false);

    // Automatically commit auto-fill value to the parent state ONCE if current value is empty
    useEffect(() => {
        // Only proceed if an auto-fill value exists; otherwise, rely on the parent's initialization
        if (question?.autoFillValue == null || question?.autoFillValue === '') return;

        const currentValue = allFields[question.accessor];
        if (!hasAutoFilled.current && (currentValue === undefined || currentValue === null || currentValue === '')) {
            const formattedValue = formatAutoFillDisplayValue(question, question.autoFillValue);
            if (formattedValue) {
                hasAutoFilled.current = true;
                setAllFieldsValue(question.accessor, formattedValue);
                // Also reset validation states as in manual select
                if (setIsReadmissionValid) setIsReadmissionValid(false);
                if (setIsDateOfADTValid) setIsDateOfADTValid(false);
            }
        }
    }, [question?.autoFillValue, question.accessor, setAllFieldsValue]); // eslint-disable-line react-hooks/exhaustive-deps

    const questionAutoFillValue = useMemo(() => {
        return question?.autoFillValue ? toDisplayTime(question?.autoFillValue) ?? null : "";
    }, [question]);

    const onSelectOption = (val) => {
        setAllFieldsValue(question.accessor, val);
        setIsReadmissionValid(false);
        setIsDateOfADTValid(false);
    };

    return (
        <div className={`m-b-20 inputWrpr`}>
            <QuestionLabel
                handleAddHighlight={handleAddHighlight}
                accessor={question.accessor}
                allFields={allFields}
                aiEnabled={questionAiStatus[question._id] || false}
                autoFillEnabled={question?.autoFillEnabled || false}
                autoFillInfo={getAutoFillInfo(question)}
            >
                <label
                    className={`ffmr fs12 ${(checkError && !allFields[question.accessor] && question?.isRequired === true) || (question.accessor === "dateOfLatestAdmission" && isReadmissionValid) || (question.accessor === "dateOfADT" && isDateOfADTValid)
                        ? "error"
                        : null
                        }`}
                >
                    {question?.order?.label || question.label} <span style={{ fontSize: "0.8em", opacity: 0.7 }}>(MM/DD/YYYY)</span>
                </label>
            </QuestionLabel>

            <div className={questionsStyles.timeInputWrapper} style={{
                ...(isHighLightInfo ? { border: `1px solid ${isHighLightInfo}` } : {})
            }}>
                {question.invisibleAnswerOptions && (
                    <div className={questionsStyles.dropdownIcon} onClick={e => setAnchorEl(e.currentTarget)}>
                        <ArrowDropDownIcon sx={{ fontSize: '18px' }} />
                    </div>
                )}
                {(() => {
                    const val = getEffectiveValue(allFields[question.accessor], questionAutoFillValue);

                    // Determine if the value is one of the "invisible" labels
                    const invisibleLabels = question.invisibleAnswerOptions && question.dataInputLabel
                        ? question.dataInputLabel.split(',').map(opt => opt.trim()).filter(opt => opt !== '')
                        : [];
                    const isInvisibleOption = val && invisibleLabels.includes(val);

                    // If it's not an invisible option string, we treat it as a date and use Cleave for masking
                    // This allows ISO dates (with '-') and partial dates to correctly stay in the Cleave branch
                    const isDateLike = !val || !isInvisibleOption;

                    // For Cleave, we need to ensure ISO dates from the backend are converted to MM/DD/YYYY
                    // but we must avoid converting partial user input (like "03/") which toDisplayTime might break.
                    let displayVal = val;
                    if (val && !isInvisibleOption && typeof val === 'string' && val.includes('-') && val.length >= 8) {
                        const converted = toDisplayTime(val);
                        if (converted && converted !== "-") {
                            displayVal = converted;
                        }
                    }

                    if (isDateLike) {
                        return (
                            <Cleave
                                className={`ffml fs16 ${questionsStyles.customInput}`}
                                value={displayVal}
                                onChange={e => {
                                    setIsReadmissionValid(false)
                                    setIsDateOfADTValid(false)
                                    setAllFieldsValue(question.accessor, e.target.value)
                                }}
                                options={{
                                    date: true,
                                    datePattern: ["m", "d", "Y"],
                                }}
                                style={{ border: 'none' }}
                            />
                        );
                    }
                    return (
                        <input
                            className={`ffml fs16 ${questionsStyles.customInput}`}
                            value={val}
                            onChange={e => {
                                setIsReadmissionValid(false)
                                setIsDateOfADTValid(false)
                                setAllFieldsValue(question.accessor, e.target.value)
                            }}
                            style={{ border: 'none' }}
                        />
                    );
                })()}
                {(() => {
                    const val = getEffectiveValue(allFields[question.accessor], questionAutoFillValue);
                    const invisibleLabels = question.invisibleAnswerOptions && question.dataInputLabel
                        ? question.dataInputLabel.split(',').map(opt => opt.trim()).filter(opt => opt !== '')
                        : [];
                    const isInvisibleOption = val && invisibleLabels.includes(val);

                    if (isInvisibleOption) {
                        return (
                            <div
                                className={questionsStyles.clearBtn}
                                onClick={() => {
                                    setAllFieldsValue(question.accessor, '');
                                    setIsReadmissionValid(false);
                                    setIsDateOfADTValid(false);
                                }}
                            >
                                <HtmlTooltip content={"Clear Date"}>
                                    <ClearIcon sx={{ fontSize: '14px', color: '#8f8f8f', cursor: 'pointer' }} />
                                </HtmlTooltip>
                            </div>
                        );
                    }
                    return null;
                })()}
            </div>
            {question.invisibleAnswerOptions && (
                <InvisibleOptionsDropdown
                    dataInputLabel={question.dataInputLabel}
                    anchorEl={anchorEl}
                    handleClose={() => setAnchorEl(null)}
                    onSelect={onSelectOption}
                />
            )}
        </div>
    );
};

const ValidationQuestion = ({ question, allFields, setAllFieldsValue, checkError = false, handleAddHighlight, selectedProps, name = "dx", customQuestionInputType, allQuestions = [], refreshComponent, questionAiStatus = {} }) => {
    const { facilityId: facilityIdFromHook } = useFacilityId();
    const [validationOptions, setValidationOptions] = useState([]);
    const [isMedicareSelected, setIsMedicareSelected] = useState(false);

    // Check if this is the primary insurance field
    const isPrimaryInsuranceField = useMemo(() => {
        return question.accessor === "payerSourceInsurance" || question.accessor === "insurance";
    }, [question.accessor]);

    let value = allFields[question.accessor];
    // Use autoFillValue only if current value is empty/null and autoFillValue is not empty/null
    if ((!value || value === '' || value === null) && question?.autoFillValue && question?.autoFillValue !== '' && question?.autoFillValue !== null) {
        value = question?.autoFillValue;
    }

    if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
        value = value ? value?._id : null;
    } else if (question.multiple) {
        const isObjectArray = Array.isArray(value) && value.every(item => typeof item === 'object' && item !== null && !Array.isArray(item));
        if (isObjectArray) {
            value = value && value.length > 0 ? _.map(value, "_id") : [];
        } else {
            value = value && value.length > 0 ? value : [];
        }
    }

    const isHighLightInfo = useMemo(() => {
        return allFields?.highlighter?.find(item => item.fieldName === question.accessor)?.color;
        // eslint-disable-next-line
    }, [allFields]);

    // Fetch validation options for primary insurance field to check if Medicare is selected
    useEffect(() => {
        if (isPrimaryInsuranceField && question.validationType) {
            const fetchValidationOptions = async () => {
                try {
                    const facilityId = selectedProps?.selectedItem?.facilityId?._id ||
                        selectedProps?.selectedItem?.facilityId ||
                        facilityIdFromHook;
                    const options = await getValidations({
                        type: question.validationType,
                        facilityid: facilityId,
                        validationBase: question.validationBase
                    });
                    setValidationOptions(options || []);
                } catch (error) {
                    console.error('Error fetching validation options:', error);
                }
            };
            fetchValidationOptions();
        }
    }, [isPrimaryInsuranceField, question.validationType, question.validationBase, selectedProps?.selectedItem, facilityIdFromHook]);

    // Check if Medicare is selected
    useEffect(() => {
        if (!isPrimaryInsuranceField) return;

        if (isPrimaryInsuranceField && value && validationOptions.length > 0) {
            const selectedOption = validationOptions.find(opt => opt._id === value || opt._id?.toString() === value?.toString());
            const isMedicare = (allFields?.type === "admission" || allFields?.type === "readmission" || allFields?.type === "admissionOrReadmission" || allFields?.type === "return") && selectedOption?.label && selectedOption.label.trim().toLowerCase() === MEDICARE_LABEL.toLowerCase().trim();
            setIsMedicareSelected(isMedicare);

            // Set isMedicareSelected field in allFields for validation in AddPatient.js
            // Only update if the value has actually changed to prevent infinite loops
            const currentIsMedicareSelected = allFields.isMedicareSelected === true;
            if (currentIsMedicareSelected !== isMedicare) {
                setAllFieldsValue("isMedicareSelected", isMedicare);
            }

            // Clear conditional fields if Medicare is not selected
            if (!isMedicare) {
                if (allFields.medicareDaysRemaining !== undefined) {
                    setAllFieldsValue("medicareDaysRemaining", undefined);
                }
                if (allFields.secondaryInsurance !== undefined) {
                    setAllFieldsValue("secondaryInsurance", undefined);
                }
            }
        } else {
            setIsMedicareSelected(false);
            // Set isMedicareSelected to false when insurance is not selected
            // Only update if the value has actually changed to prevent infinite loops
            const currentIsMedicareSelected = allFields.isMedicareSelected === true;
            if (currentIsMedicareSelected !== false) {
                setAllFieldsValue("isMedicareSelected", false);
            }
            // Clear conditional fields if primary insurance is cleared
            if (!value && (allFields.medicareDaysRemaining !== undefined || allFields.secondaryInsurance !== undefined)) {
                if (allFields.medicareDaysRemaining !== undefined) {
                    setAllFieldsValue("medicareDaysRemaining", undefined);
                }
                if (allFields.secondaryInsurance !== undefined) {
                    setAllFieldsValue("secondaryInsurance", undefined);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, validationOptions, isPrimaryInsuranceField]);

    return (
        <>
            <div className={`m-b-20 inputWrpr`}>
                <QuestionLabel
                    handleAddHighlight={handleAddHighlight}
                    accessor={question.accessor}
                    allFields={allFields}
                    aiEnabled={questionAiStatus[question._id] || false}
                    autoFillEnabled={question?.autoFillEnabled || false}
                    autoFillInfo={getAutoFillInfo(question)}
                >
                    <label
                        className={`ffmr fs12 ${checkError && !allFields[question.accessor] && question?.isRequired === true
                            ? "error"
                            : null
                            }`}
                    >
                        {question?.order?.label || question.label}
                    </label>
                </QuestionLabel>

                <Validation
                    allQuestions={allQuestions}
                    selectedItem={selectedProps?.selectedItem}
                    isHighLightInfo={isHighLightInfo}
                    type={question.validationType}
                    validationBase={question.validationBase}
                    value={value}
                    multiple={question.multiple}
                    isCustomQuestion={question?.isCustom}
                    customQuestionInputType={customQuestionInputType}
                    accessor={question.accessor}
                    questionId={question?._id}
                    refreshComponent={refreshComponent}
                    invisibleAnswerOptions={question.invisibleAnswerOptions}
                    dataInputLabel={question.dataInputLabel}
                    onChange={async val => {
                        if (question.multiple) {
                            const value = val.filter(item => item !== undefined);

                            if (value) {
                                await setAllFieldsValue(
                                    name,
                                    value.length > 0 ? value.map(v => v?._id) : []
                                );
                            } else {
                                setAllFieldsValue(name, []);
                            }
                        } else {
                            setAllFieldsValue(question.accessor, val);
                        }
                    }}
                />
            </div>

            {/* Conditional fields for Medicare */}
            {isPrimaryInsuranceField && isMedicareSelected && (
                <>
                    {/* Medicare days remaining at admission */}
                    <div className={`m-b-20 inputWrpr`}>
                        <QuestionLabel
                            handleAddHighlight={handleAddHighlight}
                            accessor="medicareDaysRemaining"
                            allFields={allFields}
                            aiEnabled={false}
                        >
                            <label
                                className={`ffmr fs12 ${checkError && (!allFields.medicareDaysRemaining || allFields.medicareDaysRemaining === '')
                                    ? "error"
                                    : null
                                    }`}
                            >
                                Medicare days remaining at admission
                            </label>
                        </QuestionLabel>
                        <TextField
                            fullWidth
                            value={allFields.medicareDaysRemaining != null ? String(allFields.medicareDaysRemaining) : ''}
                            onChange={e => {
                                const inputValue = e.target.value;
                                // Only allow integers (positive numbers)
                                if (inputValue === '' || /^\d+$/.test(inputValue)) {
                                    setAllFieldsValue("medicareDaysRemaining", inputValue === '' ? null : parseInt(inputValue, 10));
                                }
                            }}
                            variant="standard"
                            autoComplete="off"
                            type="text"
                            InputProps={{
                                disableUnderline: true,
                            }}
                            sx={{
                                "input": {
                                    borderRadius: "4px",
                                    border: "none !important",
                                    color: "#000000 !important",
                                },
                                '& .MuiInputBase-root': {
                                    height: "40px",
                                    borderRadius: "4px",
                                    color: "#000000 !important",
                                },
                                '& .MuiInputBase-input': {
                                    color: "#000000 !important",
                                },
                            }}
                        />
                    </div>

                    {/* Secondary insurance */}
                    <div className={`m-b-20 inputWrpr`}>
                        <QuestionLabel
                            handleAddHighlight={handleAddHighlight}
                            accessor="secondaryInsurance"
                            allFields={allFields}
                            aiEnabled={false}
                        >
                            <label
                                className={`ffmr fs12 ${checkError && !allFields.secondaryInsurance
                                    ? "error"
                                    : null
                                    }`}
                            >
                                Secondary insurance
                            </label>
                        </QuestionLabel>
                        <Validation
                            allQuestions={allQuestions}
                            selectedItem={selectedProps?.selectedItem}
                            isHighLightInfo={allFields?.highlighter?.find(item => item.fieldName === "secondaryInsurance")?.color}
                            type={question.validationType}
                            validationBase={question.validationBase}
                            value={allFields.secondaryInsurance}
                            multiple={false}
                            isCustomQuestion={false}
                            customQuestionInputType={customQuestionInputType}
                            accessor="secondaryInsurance"
                            questionId={question?._id}
                            refreshComponent={refreshComponent}
                            onChange={async val => {
                                setAllFieldsValue("secondaryInsurance", val);
                            }}
                        />
                    </div>
                </>
            )}
        </>
    );
};

const ToggleQuestion = ({ question, allFields, setAllFieldsValue, checkError = false, isForceRequired = false, handleAddHighlight, questionAiStatus = {} }) => {
    const isHighLightInfo = useMemo(() => {
        return allFields?.highlighter?.find(item => item.fieldName === question.accessor)?.color;
        // eslint-disable-next-line
    }, [allFields]);
    return (
        <div className={`m-b-20 inputWrpr`}>
            <QuestionLabel
                handleAddHighlight={handleAddHighlight}
                accessor={question.accessor}
                allFields={allFields}
                aiEnabled={questionAiStatus[question._id] || false}
                autoFillEnabled={question?.autoFillEnabled || false}
                autoFillInfo={getAutoFillInfo(question)}
            >
                <label
                    className={`ffmr fs12 ${checkError &&
                        (allFields[question.accessor] === undefined || allFields[question.accessor] == null || isForceRequired || (allFields?.type === "transfer" && allFields?.transferType === "hospitalTransfer")) &&
                        (question?.isRequired === true || isForceRequired)
                        ? "error"
                        : null
                        }`}
                >
                    {question?.order?.label || question.label}
                </label>
            </QuestionLabel>
            <div className={`df aic ffml fs14 ${styles.toggleWrpr}`}
                style={{
                    ...(isHighLightInfo && {
                        border: `1px solid ${isHighLightInfo}`,
                        borderRadius: "4px",
                    }),
                }}
            >
                {question.options.map(option => {
                    const currentValue = getEffectiveValue(allFields[question.accessor], question?.autoFillValue);
                    return (
                        <div
                            key={option.value}
                            className={`${styles.sec} ${currentValue === option.value ? styles.selected : ""
                                }`}
                            onClick={() => setAllFieldsValue(question.accessor, option.value)}
                        >
                            {option.label}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const TimeQuestion = ({ question, allFields, setAllFieldsValue, checkError = false, handleAddHighlight, questionAiStatus = {} }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [showTimeSelect, setShowTimeSelect] = useState(false);

    const initialTime = getEffectiveValue(allFields[question.accessor], question?.autoFillValue) || undefined;
    const [time, setTime] = useState(initialTime || undefined);
    const timePickerRef = useRef(null);

    const isHighLightInfo = useMemo(() => {
        return allFields?.highlighter?.find(item => item.fieldName === question.accessor)?.color;
        // eslint-disable-next-line
    }, [allFields]);

    const hasAutoFilled = useRef(false);

    // Automatically commit auto-fill value to the parent state ONCE if current value is empty
    useEffect(() => {
        // Only proceed if an auto-fill value exists; otherwise, rely on the parent's initialization
        if (question?.autoFillValue == null || question?.autoFillValue === '') return;

        const currentValue = allFields[question.accessor];
        if (!hasAutoFilled.current && (currentValue === undefined || currentValue === null || currentValue === '')) {
            const formattedValue = formatAutoFillDisplayValue(question, question.autoFillValue);
            if (formattedValue) {
                hasAutoFilled.current = true;
                setAllFieldsValue(question.accessor, formattedValue);
            }
        }
    }, [question?.autoFillValue, question.accessor, setAllFieldsValue]); // eslint-disable-line react-hooks/exhaustive-deps

    // Update local 'time' state whenever the specific field value or auto-fill value changes
    useEffect(() => {
        const newTime = getEffectiveValue(allFields[question.accessor], question?.autoFillValue) || undefined;
        setTime(newTime || undefined);
    }, [allFields[question.accessor], question?.autoFillValue]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const outsideClick = e => {
            showTimeSelect &&
                timePickerRef.current &&
                !timePickerRef.current.contains(e.target) &&
                setShowTimeSelect(false);
        };

        document.addEventListener("mousedown", outsideClick);

        return () => document.removeEventListener("mousedown", outsideClick);
    }, [showTimeSelect]);

    return (
        <>
            <div className={`m-b-20 inputWrpr`}>
                <QuestionLabel
                    handleAddHighlight={handleAddHighlight}
                    accessor={question.accessor}
                    allFields={allFields}
                    aiEnabled={questionAiStatus[question._id] || false}
                    autoFillEnabled={question?.autoFillEnabled || false}
                    autoFillInfo={getAutoFillInfo(question)}
                >
                    <label
                        className={`ffmr fs12 ${checkError && !allFields[question.accessor] && question?.isRequired === true
                            ? "error"
                            : null
                            }`}
                    >
                        {question?.order?.label || question.label}
                    </label>
                </QuestionLabel>

                <div className={questionsStyles.timeInputWrapper} style={{
                    ...(isHighLightInfo && {
                        border: `1px solid ${isHighLightInfo}`,
                        borderRadius: "4px",
                    }),
                }}>
                    {question.invisibleAnswerOptions && (
                        <div className={questionsStyles.dropdownIcon} onClick={e => setAnchorEl(e.currentTarget)}>
                            <ArrowDropDownIcon sx={{ fontSize: '18px' }} />
                        </div>
                    )}
                    <input
                        readOnly={true}
                        onClick={() => setShowTimeSelect(!showTimeSelect)}
                        className={classNames(`ffml fs16 ${questionsStyles.customInput}`)}
                        value={time}
                        placeholder="Select Time"
                        style={{ border: 'none' }}
                        onChange={e => setAllFieldsValue(question.accessor, e.target.value)}
                    />
                    {(() => {
                        const invisibleLabels = question.invisibleAnswerOptions && question.dataInputLabel
                            ? question.dataInputLabel.split(',').map(opt => opt.trim()).filter(opt => opt !== '')
                            : [];
                        const isInvisibleOption = time && invisibleLabels.includes(time);

                        if (isInvisibleOption) {
                            return (
                                <div
                                    className={questionsStyles.clearBtn}
                                    onClick={() => {
                                        setTime("");
                                        setAllFieldsValue(question.accessor, '');
                                    }}
                                >
                                    <HtmlTooltip content={"Clear Time"}>
                                        <ClearIcon sx={{ fontSize: '14px', color: '#8f8f8f', cursor: 'pointer' }} />
                                    </HtmlTooltip>
                                </div>
                            );
                        }
                        return null;
                    })()}
                </div>
                {question.invisibleAnswerOptions && (
                    <InvisibleOptionsDropdown
                        dataInputLabel={question.dataInputLabel}
                        anchorEl={anchorEl}
                        handleClose={() => setAnchorEl(null)}
                        onSelect={(val) => {
                            setTime(val);
                            setAllFieldsValue(question.accessor, val);
                        }}
                    />
                )}
            </div>
            {showTimeSelect ? (
                <>
                    <div className={styles.timePickerOverlay} />
                    <div className={styles.timePickerPopup} ref={timePickerRef}>
                        <TimeKeeper
                            // time={allFields[question.accessor] || "12:00pm"}
                            switchToMinuteOnHourSelect
                            onChange={data => {
                                setTime(data.formatted12);
                                return setAllFieldsValue(question.accessor, data.formatted12);
                            }}
                            doneButton={newTime => (
                                <div
                                    style={{
                                        textAlign: "center",
                                        padding: "10px 0",
                                        cursor: "pointer",
                                    }}
                                    onClick={() => {
                                        setTime(newTime.formatted12);
                                        setAllFieldsValue(question.accessor, newTime.formatted12);
                                        setShowTimeSelect(false);
                                    }}
                                >
                                    Done
                                </div>
                            )}
                        />
                    </div>
                </>
            ) : undefined}
        </>
    );
};

const Questions = props => {
    const { checkError = false, isAskAdmissionDate, selectedItem, isOnlyHospitalDashboard,
        setIsReadmissionValid,
        isReadmissionValid,
        validation,
        setIsDateOfADTValid,
        isDateOfADTValid,
        aiData,
        onAiDataUpdate,
        aiDataLoading,
        questionAiStatus,
        setQuestionAiStatus
    } = props;
    const activeFacilities = useSelector(
        (state) => state.activeFacilities.facilities
    );
    const { facilityId: facilityIdFromHook } = useFacilityId();
    const [questions, setQuestions] = useState([]);
    const [expanded, setExpanded] = useState(false);
    //const [allFields, setAllFields] = useState(props.allFields);
    const allFields = useMemo(() => {
        return props.allFields
    }, [props.allFields]);

    const getQuestions = async (propsAllFields) => {
        let transferType = propsAllFields?.transferType;

        if (propsAllFields && (propsAllFields.transferType === "unplannedHospitalTransfer" || propsAllFields.transferType === "plannedHospitalTransfer")) {
            transferType = "hospitalTransfer"
        }
        const res = await getAllQuestions({
            facilityid: facilityIdFromHook,
            forType: propsAllFields?.type === "readmission" ? "admission" : propsAllFields?.type,
            autoFilledData: _.pick(propsAllFields, ["firstName", "lastName", "DOB", "dateOfADT", "_id"]),
            ...propsAllFields?.type === "transfer" && { forTransferType: transferType }
        })
        if (res && res.length > 0) {
            const filteredQuestions = res.filter(ele => !ele?.question?.isDefaultCard && !ele?.question?.existingAccessor);
            setQuestions(filteredQuestions);
            if (props.setQuestions) {
                props.setQuestions(filteredQuestions);
            }
            // Load AI status for all questions
            loadQuestionAiStatus(filteredQuestions);
        }
    };

    const loadQuestionAiStatus = useCallback(async (questions) => {
        if (!questions || questions.length === 0) return;

        try {
            const questionIds = questions.map(q => q.question._id);
            const facilityIds = activeFacilities;

            const response = await api.post('/api/ai-autofill/ai-status-batch', {
                questionIds,
                facilityIds
            });

            if (response?.success) {
                const aiStatusMap = {};
                response.data.forEach(item => {
                    aiStatusMap[item.questionId] = item.aiEnabled;
                });
                setQuestionAiStatus(aiStatusMap);
            }
        } catch (error) {
            console.error('Error loading AI status:', error);
            // Don't show error to user, just continue without AI icons
        }
    }, [setQuestionAiStatus, activeFacilities]);

    useEffect(() => {
        // Only call getQuestions when essential fields change, not on every allFields change
        const essentialFields = {
            type: props.allFields?.type,
            transferType: props.allFields?.transferType,
            firstName: props.allFields?.firstName,
            lastName: props.allFields?.lastName,
            DOB: props.allFields?.DOB,
            dateOfADT: props.allFields?.dateOfADT
        };

        getQuestions(essentialFields);
    }, [props.allFields?.type, props.allFields?.transferType, props.allFields?.firstName, props.allFields?.lastName, props.allFields?.DOB, props.allFields?.dateOfADT]); // eslint-disable-line

    const ifDepends = question => {
        const { dependsOn, dependsOnValues, accessor } = question;

        if (!dependsOn) return true;
        if (dependsOn === "server") {
            if (selectedItem || isOnlyHospitalDashboard) {
                return isAskAdmissionDate || allFields[accessor] != null ? true : false;
            } else {
                return isAskAdmissionDate ? true : false;
            }
        } else {
            return dependsOnValues.includes(allFields[dependsOn]);
        }
    };

    const handleClickExpand = useCallback((textValue, accessor) => {
        setExpanded({ value: textValue, accessor });
    }, [setExpanded]);


    const renderQuestion = (question) => {
        if (question?.customQuestionInputType) {
            if (question.customQuestionInputType === QUESTION_INPUT_TYPE.UNLIMITED_ANSWERS) {
                return (
                    <>
                        {ifDepends(question) ? (
                            <ValidationQuestion
                                allQuestions={question?.allQuestions}
                                question={question}
                                allFields={props.allFields}
                                selectedProps={props}
                                name={question?.accessor}
                                setAllFieldsValue={props.setAllFieldsValue}
                                checkError={checkError}
                                handleAddHighlight={props.handleAddHighlight}
                                customQuestionInputType={question.customQuestionInputType}
                                refreshComponent={props?.refreshComponent}
                                questionAiStatus={questionAiStatus}
                            />
                        ) : undefined}
                    </>
                );
            } else if (question.customQuestionInputType === QUESTION_INPUT_TYPE.LIMITED_ANSWERS) {
                return (
                    <SelectInputField
                        question={question}
                        allFields={props.allFields}
                        setAllFieldsValue={props.setAllFieldsValue}
                        checkError={checkError}
                        handleClickExpand={handleClickExpand}
                        handleAddHighlight={props.handleAddHighlight}
                        questionAiStatus={questionAiStatus}
                    />
                )
            } else if (question.customQuestionInputType === QUESTION_INPUT_TYPE.NUMBER_RANGE) {
                return <TextQuestion
                    isOnlyNumber={question.customQuestionInputType === QUESTION_INPUT_TYPE.NUMBER_RANGE}
                    question={question}
                    allFields={props.allFields}
                    setAllFieldsValue={props.setAllFieldsValue}
                    checkError={checkError}
                    handleClickExpand={handleClickExpand}
                    handleAddHighlight={props.handleAddHighlight}
                    customQuestion={true}
                    questionAiStatus={questionAiStatus}
                />
            } else if (question.customQuestionInputType === QUESTION_INPUT_TYPE.NUMBER_RANGE_LIMITED_ANSWERS) {
                return <NumberAndSelectQuestion
                    question={question}
                    allFields={props.allFields}
                    setAllFieldsValue={props.setAllFieldsValue}
                    checkError={checkError}
                    handleClickExpand={handleClickExpand}
                    handleAddHighlight={props.handleAddHighlight}
                    questionAiStatus={questionAiStatus}
                />
            }
        }


        switch (question.type) {
            case "text":
                return (
                    <>
                        {ifDepends(question) ? (
                            <TextQuestion
                                question={question}
                                allFields={props.allFields}
                                setAllFieldsValue={props.setAllFieldsValue}
                                checkError={checkError}
                                handleClickExpand={handleClickExpand}
                                handleAddHighlight={props.handleAddHighlight}
                                questionAiStatus={questionAiStatus}
                            />
                        ) : undefined}
                    </>
                );
            case "date":
                return (
                    <>
                        {ifDepends(question) ? (
                            <DateQuestion
                                question={question}
                                allFields={props.allFields}
                                setAllFieldsValue={props.setAllFieldsValue}
                                checkError={checkError}
                                handleAddHighlight={props.handleAddHighlight}
                                validation={validation}
                                setIsReadmissionValid={setIsReadmissionValid}
                                isReadmissionValid={isReadmissionValid}
                                setIsDateOfADTValid={setIsDateOfADTValid}
                                isDateOfADTValid={isDateOfADTValid}
                                questionAiStatus={questionAiStatus}
                            />
                        ) : undefined}
                    </>
                );
            case "validation":
                return (
                    <>
                        {ifDepends(question) ? (
                            <ValidationQuestion
                                allQuestions={question?.allQuestions}
                                question={question}
                                allFields={props.allFields}
                                selectedProps={props}
                                setAllFieldsValue={props.setAllFieldsValue}
                                checkError={checkError}
                                handleAddHighlight={props.handleAddHighlight}
                                refreshComponent={props?.refreshComponent}
                                questionAiStatus={questionAiStatus}
                            />
                        ) : undefined}
                    </>
                );

            case "toggle":
                return (
                    <>
                        {ifDepends(question) ? (
                            <ToggleQuestion
                                question={question}
                                allFields={props.allFields}
                                setAllFieldsValue={props.setAllFieldsValue}
                                checkError={checkError}
                                handleAddHighlight={props.handleAddHighlight}
                                questionAiStatus={questionAiStatus}
                            />
                        ) : undefined}
                    </>
                );

            case "time":
                return (
                    <>
                        {ifDepends(question) ? (
                            <TimeQuestion
                                question={question}
                                allFields={props.allFields}
                                setAllFieldsValue={props.setAllFieldsValue}
                                checkError={checkError}
                                handleAddHighlight={props.handleAddHighlight}
                                questionAiStatus={questionAiStatus}
                            />
                        ) : undefined}
                    </>
                );
            default:
                return undefined;
        }
    };

    // Check if any question has AI enabled
    const isAiEnabled = hasAiEnabledQuestions(questions, questionAiStatus);

    // State for AI sidebar visibility
    const [isAiSidebarOpen, setIsAiSidebarOpen] = useState(true);

    // Handlers for AI sidebar
    const handleOpenAiSidebar = () => {
        setIsAiSidebarOpen(true);
    };

    const handleCloseAiSidebar = () => {
        setIsAiSidebarOpen(false);
    };

    // Create form context for AI
    const formContext = {
        type: props.allFields?.type,
        transferType: props.allFields?.transferType,
        firstName: props.allFields?.firstName,
        lastName: props.allFields?.lastName,
        DOB: props.allFields?.DOB,
        dateOfADT: props.allFields?.dateOfADT
    };

    // Create available fields array for AI
    const availableFields = useMemo(() => {
        if (!questions) return [];

        return questions
            .filter(q => {
                // Only include questions that have AI enabled for the current facility
                return questionAiStatus[q.question._id] === true;
            })
            .map(q => {
                return {
                    accessor: q.question.accessor,
                    label: q?.order?.label || q.question.label,
                    type: q.question.type,
                    isRequired: q.question.isRequired,
                    aiEnabled: true, // All fields in this array are AI-enabled
                    customQuestionInputType: q.question.customQuestionInputType,
                    isCustom: q.question.isCustom || false,
                    validationType: q.question.validationType,
                    validationBase: q.question.validationBase,
                    customQuestionOptions: q.question.customQuestionOptions || []
                };
            });
    }, [questions, questionAiStatus]);

    // Helper function to find the best matching toggle option
    const findBestToggleOption = (aiValue, options) => {
        if (!aiValue || !options || !Array.isArray(options)) {
            return null;
        }

        const normalizedAiValue = String(aiValue).toLowerCase().trim();

        // First, try exact match on value
        let exactValueMatch = options.find(option =>
            String(option.value).toLowerCase() === normalizedAiValue
        );
        if (exactValueMatch) {
            return exactValueMatch;
        }

        // Second, try exact match on label
        let exactLabelMatch = options.find(option =>
            String(option.label).toLowerCase() === normalizedAiValue
        );
        if (exactLabelMatch) {
            return exactLabelMatch;
        }

        // Third, try partial/fuzzy matching on label
        let partialMatch = options.find(option => {
            const optionLabel = String(option.label).toLowerCase();
            return optionLabel.includes(normalizedAiValue) || normalizedAiValue.includes(optionLabel);
        });
        if (partialMatch) {
            return partialMatch;
        }

        // Fourth, try common boolean/toggle mappings
        const booleanMappings = {
            'yes': ['yes', 'true', '1', 'on', 'enabled', 'active'],
            'no': ['no', 'false', '0', 'off', 'disabled', 'inactive'],
            'true': ['yes', 'true', '1', 'on', 'enabled', 'active'],
            'false': ['no', 'false', '0', 'off', 'disabled', 'inactive']
        };

        for (const option of options) {
            const optionValue = String(option.value).toLowerCase();
            const optionLabel = String(option.label).toLowerCase();

            // Check if this option matches any boolean mapping
            for (const [key, synonyms] of Object.entries(booleanMappings)) {
                if ((optionValue === key || optionLabel === key) && synonyms.includes(normalizedAiValue)) {
                    return option;
                }
            }
        }

        return null;
    };

    // Helper function to convert validation label to ID
    const convertValidationLabelToId = async (label, validationType, validationBase) => {
        try {
            // Get the facility ID
            const facilityId = facilityIdFromHook;

            // Fetch validation options
            const validationOptions = await getValidations({
                type: validationType,
                facilityid: facilityId,
                validationBase: validationBase
            });

            // Find the option that matches the label
            const matchingOption = validationOptions.find(option =>
                option.label.toLowerCase() === label.toLowerCase()
            );

            return matchingOption ? matchingOption._id : null;
        } catch (error) {
            console.error('Error fetching validation options:', error);
            return null;
        }
    };

    const handleApplySuggestions = async (suggestions) => {
        // Apply AI suggestions to form fields
        for (const [fieldName, value] of Object.entries(suggestions)) {
            // Find the question to check if it's a validation field
            const question = questions?.find(q => q.question.accessor === fieldName);

            if (question?.question?.type === 'validation' && question?.question?.validationType) {
                // For regular validation fields, we need to convert label to ID
                try {
                    const validationId = await convertValidationLabelToId(
                        value,
                        question.question.validationType,
                        question.question.validationBase
                    );

                    if (validationId) {
                        props.setAllFieldsValue(fieldName, validationId);
                    } else {
                        console.warn(`Could not find validation ID for field ${fieldName} with value: ${value}`);
                        // Still set the value as-is in case it's already an ID
                        props.setAllFieldsValue(fieldName, value);
                    }
                } catch (error) {
                    console.error(`Error converting validation label to ID for ${fieldName}:`, error);
                    // Fallback to setting the value as-is
                    props.setAllFieldsValue(fieldName, value);
                }
            } else if (question?.question?.customQuestionInputType === QUESTION_INPUT_TYPE.UNLIMITED_ANSWERS) {
                // For UNLIMITED_ANSWERS questions, the value is already a validation ID from AiChatSidebar
                props.setAllFieldsValue(fieldName, value);
            } else if (question?.question?.customQuestionInputType === QUESTION_INPUT_TYPE.LIMITED_ANSWERS) {
                // For LIMITED_ANSWERS questions, the value should be from predefined options only
                props.setAllFieldsValue(fieldName, value);
            } else if (question?.question?.customQuestionInputType === QUESTION_INPUT_TYPE.NUMBER_RANGE_LIMITED_ANSWERS) {
                // For NUMBER_RANGE_LIMITED_ANSWERS questions, the value should be from predefined numeric options only
                props.setAllFieldsValue(fieldName, value);
            } else if (question?.question?.type === 'toggle') {
                // For toggle questions, match the AI suggestion to the correct option value

                const matchedOption = findBestToggleOption(value, question?.question?.options);
                if (matchedOption) {
                    props.setAllFieldsValue(fieldName, matchedOption.value);
                } else {
                    console.warn(`Could not match toggle value "${value}" to any option for field ${fieldName}`);
                    // Try to set the value directly in case it matches an option value
                    props.setAllFieldsValue(fieldName, value);
                }
            } else {
                // For non-validation fields, set the value directly
                props.setAllFieldsValue(fieldName, value);
            }
        }
    };

    return (
        <div style={{ display: 'flex', height: isAiEnabled ? '100vh' : 'auto', gap: '16px' }}>
            {/* Main Questions Area */}
            <div style={{
                width: isAiEnabled ? '90%' : '100%',
                overflow: isAiEnabled ? 'auto' : 'visible',
                paddingRight: isAiEnabled ? '8px' : '0'
            }}>

                <div>
                    {questions && questions.length > 0 && (
                        <>
                            {questions.map((question, idx) => {
                                return (
                                    <React.Fragment key={`idx-${idx}`}>
                                        {renderQuestion({
                                            ...question.question,
                                            order: question?.order,
                                            allQuestions: questions,
                                            sourceQuestion: question?.sourceQuestion,
                                        })}
                                    </React.Fragment>
                                );
                            })}
                        </>
                    )}
                </div>

                {!!expanded && (
                    <AddPatientTextPopup
                        title="Details"
                        isConfirm={false}
                        content={expanded?.value}
                        handleClose={() => setExpanded(null)}
                        handleSubmit={(valueData) => {
                            props.setAllFieldsValue(expanded?.accessor, valueData);
                            setExpanded(null);
                        }}
                    />
                )}
            </div>

            {/* AI Chat Sidebar - Only show when AI is enabled and sidebar is open */}
            {isAiEnabled && isAiSidebarOpen && (
                <div style={{ width: '10%', minWidth: '400px' }}>
                    <AiChatSidebar
                        formContext={formContext}
                        availableFields={availableFields}
                        onApplySuggestions={handleApplySuggestions}
                        facilityId={facilityIdFromHook}
                        disabled={checkError}
                        onClose={handleCloseAiSidebar}
                        questions={questions}
                        refreshComponent={props?.refreshComponent}
                        aiData={aiData}
                        onAiDataUpdate={onAiDataUpdate}
                        aiDataLoading={aiDataLoading}
                    />
                </div>
            )}

            {/* Floating AI Icon - Show when AI is enabled but sidebar is closed */}
            {isAiEnabled && !isAiSidebarOpen && (
                <FloatingAiIcon
                    onClick={handleOpenAiSidebar}
                    disabled={checkError}
                />
            )}
        </div>
    );
};

export default Questions;
